/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';

import { ErrorBoundary } from '@lumapps/utils/components/ErrorBoundary';
import { Widget } from '@lumapps/widget-base/components/Widget';
import { WidgetSkeleton } from '@lumapps/widget-base/components/WidgetSkeleton';

import * as types from '../../types';
import { Cell } from '../Cell';
import { Row } from '../Row';

type NodeTypes = types.Row | types.Cell | types.WidgetReference;

/** Render node (cell, row & widget) recursively. */
function renderNode(node: NodeTypes, index: number, path: types.LayoutPath, widgetOverrides?: types.WidgetOverrides) {
    if (types.isRow(node)) {
        const { cells, ...props } = node;
        const newPath: types.LayoutPath = [...path, `row${index}`];
        return (
            <ErrorBoundary key={index}>
                <Row path={newPath.join('-')} {...props}>
                    {renderTree(cells, widgetOverrides, newPath)}
                </Row>
            </ErrorBoundary>
        );
    }

    if (types.isCell(node)) {
        const { components, ...props } = node;
        const newPath: types.LayoutPath = [...path, `cell${index}`];

        return components?.length ? (
            <ErrorBoundary key={index}>
                <Cell path={newPath.join('-')} {...props}>
                    {renderTree(components, widgetOverrides, newPath)}
                </Cell>
            </ErrorBoundary>
        ) : null;
    }

    const DefaultWidgetOverride = widgetOverrides?.default;
    const WidgetOverrides = widgetOverrides?.[node.widgetType] || DefaultWidgetOverride;

    const newPath: types.LayoutPath = [...path, `widget${index}`];

    return (
        <ErrorBoundary key={index}>
            {WidgetOverrides ? (
                <React.Suspense
                    fallback={
                        DefaultWidgetOverride ? (
                            <DefaultWidgetOverride widgetReference={node} path={newPath}>
                                <WidgetSkeleton widgetId={node.widgetId} widgetType={node.widgetType} />
                            </DefaultWidgetOverride>
                        ) : null
                    }
                >
                    <WidgetOverrides widgetReference={node} path={newPath} />
                </React.Suspense>
            ) : (
                <Widget {...node} />
            )}
        </ErrorBoundary>
    );
}

/** Render tree recursively. */
export function renderTree(
    components: Array<NodeTypes>,
    widgetOverrides?: types.WidgetOverrides,
    path: types.LayoutPath = [],
) {
    return components.map((node, index) => renderNode(node, index, path, widgetOverrides));
}
